// Pure classification for keyboard-wedge scan bursts. Extracted from
// useBarcodeScanner so the human-vs-scanner decision — which now guards
// BOTH the global path and the capture-from-focused-input path — is unit
// tested (node --test, same pattern as terminalPollWatchdog.core).
//
// The thresholds are field-tuned legacy parity (waio-portal
// jquery.scannerdetection wrapper): a scanner "types" its whole code in a
// tight burst; humans can't sustain the per-char rate over minLength chars.

export interface BurstGates {
  minLength: number
  avgTimeByCharMs: number
  maxDurationMs: number
}

export type BurstVerdict = 'scan' | 'too_short' | 'too_slow'

export function classifyBurst(
  length: number,
  elapsedMs: number,
  gates: BurstGates,
): BurstVerdict {
  if (length < gates.minLength) return 'too_short'
  if (elapsedMs > length * gates.avgTimeByCharMs) return 'too_slow'
  if (elapsedMs > gates.maxDurationMs) return 'too_slow'
  return 'scan'
}

// ---- Enter-key resolution ---------------------------------------------
//
// What the hook must DO at the terminator, as pure data — so the branch
// matrix (verdict × focused-input × restorable × cooldown) is unit tested
// and the hook's DOM layer just executes the returned action.

export interface EnterContext {
  verdict: BurstVerdict
  /** A restorable text input is focused at Enter time. */
  inInput: boolean
  /** Whole burst landed in that input and a pre-burst snapshot exists. */
  restorable: boolean
  /** Verdict is 'scan' but we're inside the post-scan cooldown window. */
  cooldownHit: boolean
}

export interface EnterAction {
  /** preventDefault + stopPropagation the Enter. */
  swallowEnter: boolean
  /** Restore the input to its pre-burst snapshot. */
  restoreInput: boolean
  /** Blur the input (scan implies scan posture). */
  blurInput: boolean
  /** Fire onScan with the buffer. */
  dispatch: boolean
  /**
   * Which diagnostic to emit; null = silent. In-input human typing is
   * deliberately silent — it's every normal search submit, and console
   * output ships to PostHog replay.
   */
  log: 'accepted' | 'captured-from-input' | 'cooldown' | 'too_short' | 'too_slow' | null
}

export function resolveEnterAction(ctx: EnterContext): EnterAction {
  const none: EnterAction = {
    swallowEnter: false,
    restoreInput: false,
    blurInput: false,
    dispatch: false,
    log: null,
  }

  if (ctx.verdict !== 'scan') {
    // Human typing (or noise). In an input: completely inert — the Enter
    // keeps its native semantics. Globally: keep the rejection
    // diagnostics that scan-not-working tickets rely on.
    if (ctx.inInput) return none
    return { ...none, log: ctx.verdict }
  }

  if (ctx.cooldownHit) {
    // Swallow so the Enter can't click whatever is focused, and clean
    // the burst back out of the input on a double-scan.
    return {
      ...none,
      swallowEnter: true,
      restoreInput: ctx.restorable,
      log: 'cooldown',
    }
  }

  return {
    swallowEnter: true,
    restoreInput: ctx.restorable,
    blurInput: ctx.restorable,
    dispatch: true,
    log: ctx.restorable ? 'captured-from-input' : 'accepted',
  }
}
